import client from "api/WookAxios";
import { DELIVERY_LABEL, VERIFICATION_CHANNEL } from "constants/auth";
import { sessionStorageAdapter } from "utils/storage";
import { useCountdown } from "hooks/useCountdown";
import { handleThrottle } from "lib/errors/handle";
import { parseErrors } from "lib/errors/parse";
import log from "loglevel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { normalizeEmail, normalizeMobile } from "utils/normalize";
import { attachParticle, getParticle } from "utils/text";
import { formatDuration } from "utils/time";
import { formatMobile } from "utils/format";
import { toastAlert, toastInfo } from "lib/toast";

const CHANNEL_CONFIG = {
  [VERIFICATION_CHANNEL.EMAIL]: {
    label: '이메일',
    normalize: normalizeEmail,
  },
  [VERIFICATION_CHANNEL.MOBILE]: {
    label: '전화번호',
    normalize: normalizeMobile,
  },
};

const getChannelConfig = (channel) => {
  const baseConfig = CHANNEL_CONFIG[channel];
  if (!baseConfig) {
    throw new Error(`Unknown verification channel: ${channel}`);
  }

  return {
    ...baseConfig,
    particle: getParticle(baseConfig.label),
  }
}

const adapter = sessionStorageAdapter;

export const useVerification = (channel) => {
  const config = useMemo(() => getChannelConfig(channel), [channel]);
  const [codeSent, setCodeSent] = useState(false);
  const targetRef = useRef(null);
  const codeRef = useRef(null);
  const targetStorageKey = `verification::${channel}::target`;
  
  const onExpire = useCallback(() => {
    codeRef.current.value = '';
    toast.info('입력 가능 시간을 초과하였습니다. 다시 시도해 주세요.');
  }, [])
  
  const verificationCountdown = useCountdown({onExpire, storageKey: `verification::${channel}::expiresAt`});
  const cooldownCountdown = useCountdown({storageKey: `verification::${channel}::cooldownExpiresAt`});

  const remainingTime = formatDuration(verificationCountdown.remaining);
  const cooldownSeconds = Math.floor(cooldownCountdown.remaining / 1000);

  const validateTarget = useCallback(() => {
    const target = targetRef.current.value;
    if (!target) {
      toast.error(`${config.label}${config.particle} 입력해 주세요.`);
      return null;
    }

    const normalizedTarget = config.normalize(target);
    if (!normalizedTarget) {
      toast.error(`올바른 형식의 ${config.label}${config.particle} 입력해 주세요.`);
      return null;
    }

    return normalizedTarget;
  }, [config]);

  const validateCode = useCallback(() => {
    const codeValue = codeRef.current.value;
    if (!codeValue) {
      toast.error('인증번호를 입력하세요');
      return null;
    }
    
    const code = codeValue.replace(/\D/g, '');
    if (!/^\d{6}$/.test(code)) {
      toast.error('6자리 숫자를 입력하세요');
      return null;
    }

    return code;
  }, []);

  const notifyCodeSent = (channel, delivery, target) => {
    const displayTarget = channel === VERIFICATION_CHANNEL.EMAIL ? target : formatMobile(target);
    const deliveryLabel = DELIVERY_LABEL[delivery];
    if (!deliveryLabel) {
      log.warn('VERIFICATION_UNKNOWN_DELIVERY', delivery);
    }

    const targetText = attachParticle(displayTarget, '으로/로');
    const deliveryText = attachParticle(deliveryLabel ?? '인증수단', '을/를');
    toastInfo(`${targetText} 인증번호를 전송했습니다.\n${deliveryText} 확인해 주세요`);
  };

  const sendCode = async () => {
    if (cooldownCountdown.running) {
      toast.info(`${cooldownSeconds}초 후 다시 전송할 수 있습니다.`);
      return;
    }

    const normalizedTarget = validateTarget();
    if (!normalizedTarget) return;
    
    const payload = {
      channel,
      target: normalizedTarget,
    };

    try {
      const response = await client.verificationCodes(payload);
      log.debug(`SEND_${channel.toUpperCase()}_VERIFICATION_CODE_SUCCESS`, response);
      
      const { server_time, expires_at, cooldown_at } = response.data;
      const offset = server_time - Date.now();
      const localExpiresAt = expires_at - offset;
      const localCooldownAt = cooldown_at - offset;

      verificationCountdown.start(localExpiresAt);
      cooldownCountdown.start(localCooldownAt);

      adapter.save(targetStorageKey, normalizedTarget);

      setCodeSent(true);
      notifyCodeSent(channel, response.data.delivery, normalizedTarget);
    } catch(e) {
      log.debug(`SEND_${channel.toUpperCase()}_VERIFICATION_CODE_FAILED`, e);
      const throttled = handleThrottle(e.response);
      if (throttled) return;
      const errors = parseErrors(e.response);
      toast.error(errors[0].message);
    }
  };

  const resetCodeContext = () => {
    verificationCountdown.clear();
    setCodeSent(false);

    if (codeRef.current) {
      codeRef.current.value = '';
    }
  };

  const reset = () => {
    resetCodeContext();
    adapter.remove(targetStorageKey);
  };

  useEffect(() => {
    const storedTarget = adapter.load(targetStorageKey);
    if (storedTarget && targetRef.current) {
      targetRef.current.value = storedTarget;
      setCodeSent(true);
    }
  }, [targetStorageKey]);

  return {
    channel,
    targetRef,
    codeRef,
    codeSent,
    remainingTime,
    running: verificationCountdown.running,
    validateTarget,
    validateCode,
    sendCode,
    resetCodeContext,
    reset,
  }
};